import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from "@angular/core";
import { VTableRowStatus } from "@app/directives/_layout/v-table/_enums/VTableRowStatus.enum";
import { IVTableColumn } from "@app/directives/_layout/v-table/_models/IVTableColumn";
import { IVTableRow } from "@app/directives/_layout/v-table/_models/IVTableRow";
import { VTableService } from "@app/directives/_layout/v-table/_services/v-table.service";
import { SortDirection } from "@app/enum/SortDirection";
import { SortType } from "@app/enum/SortType";
import { IUser } from "@app/interfaces/IUser";
import { UserService } from "@app/main/modules/foundation/_services/user.service";
import { DateService } from "@app/services/date.service";
import { TranslationService } from "@app/services/translation.service";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { SchedulingFrequency } from "@reports/enums/SchedulingFrequency";
import { IDynamicReportSchedule } from "@reports/models/IDynamicReportSchedule";
import { IReportDefinition } from "@reports/models/IReportDefinition";
import { DynamicReportService } from "@reports/_services/dynamicReports.service";
import { lastValueFrom, Observable } from "rxjs";
import "./report-schedules.component.scss";

@UntilDestroy()
@Component({
    selector: "report-schedules",
    template: require("./report-schedules.component.html")
})
export class ReportSchedulesComponent implements OnChanges, OnInit {
    @Input() reportId: number;
    @Input() update: Observable<void>;

    @Output() scheduleClick = new EventEmitter<IDynamicReportSchedule>();
    @Output() createScheduleClick = new EventEmitter<void>();

    // ---

    schedules: IDynamicReportSchedule[];
    selectedSchedule: IDynamicReportSchedule;

    SchedulingFrequency = SchedulingFrequency;

    reports: IReportDefinition[];
    users: IUser[];

    //// #Tables
    tableColumns: IVTableColumn<IDynamicReportSchedule>[];
    tableRows: IVTableRow[];
    loading: boolean;

    // ---

    constructor(private readonly dynamicReportService: DynamicReportService,
        private readonly translationService: TranslationService,
        private readonly userService: UserService,
        private readonly vTableService: VTableService,
        private readonly dateService: DateService) {
    }

    ngOnInit(): void {
        this.createTable();
        this.loadReportSchedules();
    }

    ngOnChanges(changes: SimpleChanges): void {

        if (changes["update"] && this.update) {
            this.update.pipe(untilDestroyed(this)).subscribe(() => this.loadReportSchedules(true));
        }
    }

    onRowClicked(row: IVTableRow): void {
        this.selectedSchedule = row.data;
        this.scheduleClick.emit(row.data);
    }

    onCreateScheduleClicked(): void {
        this.createScheduleClick.emit();
    }

    reloadSchedules(): void {
        this.loadReportSchedules(true);
    }

    scheduleSaved(schedule: IDynamicReportSchedule): void {
        const existingIndex = this.schedules.findIndex(s => s.Id == schedule.Id);

        if (existingIndex >= 0) {
            this.schedules[existingIndex] = schedule;
        } else {
            this.schedules.push(schedule);
        }

        this.mapEntitiesToSchedules()
        this.tableRows = this.vTableService.createTableRows(this.schedules, this.tableColumns);
    }

    scheduleDeleted(schedule: IDynamicReportSchedule): void {
        this.schedules.splice(this.schedules.findIndex(s => s.Id == schedule.Id), 1);
        this.recreateSchedulesTable();
    }

    recreateSchedulesTable(): void {
        this.mapEntitiesToSchedules();

        this.tableRows = this.vTableService.createTableRows(this.schedules, this.tableColumns);
    }

    // ---

    private async loadReportSchedules(invalidateCache = false): Promise<void> {
        invalidateCache = true; // clearing cache for now, need to rethink - main reports-list-page can have an affect on the report schedules available
        this.loading = true;

        try {
            const [schedules, reports, users] = await Promise.all([
                this.dynamicReportService.getReportSchedules({ invalidateCache: invalidateCache, IsActiveSchedule: true }) as Promise<IDynamicReportSchedule[]>,
                this.dynamicReportService.getReports() as Promise<IReportDefinition[]>,
                lastValueFrom(this.userService.getUsers())
            ]);

            this.reports = reports;
            this.users = users;
            this.schedules = schedules;

            this.mapEntitiesToSchedules()

            if (this.reportId) {
                this.schedules = this.schedules.filter(s => s.ReportDefinitionId == this.reportId);
            }

            this.tableRows = this.vTableService.createTableRows(this.schedules, this.tableColumns);

        } finally {
            this.loading = false;
        }
    }

    private mapEntitiesToSchedules(): void {
        this.schedules.forEach(s => {
            s.reportDefinition = this.reports.find(r => r.Id == s.ReportDefinitionId);
            s.User = this.users.find(u => u.Id == s.UserId);
        });
    }

    private createTable(): void {
        this.tableColumns = [
            {
                header: this.vTableService.createStatusTableHeader({ sorted: false }),
                createCellFunction: (schedule) => this.vTableService.createStatusCell(this.getScheduleTableStatus(schedule))
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "Common.Name" }),
                createCellFunction: (schedule) => this.vTableService.createTableCell(schedule.Name),
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "Reports.Report" }),
                createCellFunction: (schedule) => this.vTableService.createTableCell(schedule.reportDefinition?.Title),
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "ReportSchedules.Frequency", minTableWidth: 920 }),
                createCellFunction: (schedule) => {
                    return this.vTableService.createTableCell(this.translationService.tEnum(SchedulingFrequency, "SchedulingFrequency", schedule.Frequency))
                },
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "Reports.LastRan", minTableWidth: 920 }),
                createCellFunction: (schedule) => this.vTableService.createDateCell(schedule.LastScheduledQueueTime, { dateTime: true }),
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "ReportSchedules.NextRun", sorted: true, sortDirection: SortDirection.Ascending, sortType: SortType.Date }),
                createCellFunction: (schedule) => {
                    let nextRunDate: Date = new Date(schedule.NextScheduledQueueTime);

                    if (this.getScheduleTableStatus(schedule) == VTableRowStatus.Error) {
                        nextRunDate = undefined;
                    }

                    return this.vTableService.createDateCell(nextRunDate, { dateTime: true })
                },
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "ReportSchedules.NextParameters", minTableWidth: 920 }),
                createCellFunction: (schedule) => {
                    if (!schedule.Parameters) {
                        this.dynamicReportService.parseScheduleParametersJson(schedule);
                    }

                    var formattedValue = `${this.dateService.formatDate(schedule.Parameters.Start, { dateTime: true })} - ${this.dateService.formatDate(schedule.Parameters.End, { dateTime: true })}`

                    return this.vTableService.createTableCell(formattedValue)
                },
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "Common.Status", minTableWidth: 1040 }),
                createCellFunction: (schedule) => this.vTableService.createTableCell(this.getScheduleStatus(schedule)),
            }
        ];
    }

    private getScheduleTableStatus(schedule: IDynamicReportSchedule): VTableRowStatus {
        switch (true) {
            case schedule.RetryCount >= 50:
                return VTableRowStatus.Error;
            case schedule.RetryCount > 0:
                return VTableRowStatus.Warning;
            default:
                return VTableRowStatus.Info;
        }
    }

    private getScheduleStatus(schedule: IDynamicReportSchedule): string {
        let status: string;

        if (schedule.RetryCount > 0) {
            status = this.translationService.t("ReportSchedules.ScheduleFailedXTimes", schedule.RetryCount);
        }

        return status;
    }
}
